import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Link from "@mui/material/Link";
import { PaletteMode, ThemeProvider, createTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { signin } from "../../api/sign";
import { KakaoIcon, OdnIcon } from "../../components/Icon/CustomIcons";
import { PasswordTextField } from "../../components/PasswordTextField";
import { ENV } from "../../config/env";
import { ErrorData } from "../../config/interfaces";
import { Card, SignInContainer } from "../../config/styles";
import TemplateFrame from "../../utils/common/TemplateFrame";
import getSignTheme from "../../utils/common/theme/getSignTheme";
import { inputValidate } from "../../utils/inputValidate";
import { intlTelInputFunc } from "../../utils/intl-tel-input";

export default function SignIn() {
  const queryParam = new URLSearchParams(window.location.search);
  const stateValue: string = queryParam.get("state") as string;
  const location = useLocation();

  const [mode, setMode] = React.useState<PaletteMode>("light");
  const [showCustomTheme, setShowCustomTheme] = React.useState(true);
  const [showPassword, setShowPassword] = React.useState(false);
  const [signinMethod, setSigninMethod] = React.useState("email");
  const [dialCode, setDialCode] = React.useState("");

  const defaultTheme = createTheme({ palette: { mode } });
  const SignInTheme = createTheme(getSignTheme(mode));

  const [usernameCheck, setUsernameCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [passwordCheck, setPasswordCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  React.useEffect(() => {
    const input = document.querySelector("#phone") as Element;
    intlTelInputFunc({ setDialCode: setDialCode, input: input });
  }, []);

  // This code only runs on the client side, to determine the system color preference
  React.useEffect(() => {
    // Check if there is a preferred mode in localStorage
    const savedMode = localStorage.getItem("themeMode") as PaletteMode | null;
    if (savedMode) {
      setMode(savedMode);
    } else {
      // If no preference is found, it uses system preference
      const systemPrefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      setMode(systemPrefersDark ? "dark" : "light");
    }
  }, []);

  const toggleColorMode = () => {
    const newMode = mode === "dark" ? "light" : "dark";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode); // Save the selected mode to localStorage
  };

  const toggleCustomTheme = () => {
    setShowCustomTheme((prev) => !prev);
  };

  const validateInputs = () => {
    if (signinMethod === "email") {
      inputValidate({
        type: "username",
        setState: setUsernameCheck,
        props: { dialCode: dialCode },
      });
    } else {
      inputValidate({
        type: "phone",
        setState: setUsernameCheck,
        props: { dialCode: dialCode },
      });
    }

    inputValidate({
      type: "password",
      setState: setPasswordCheck,
      props: { dialCode: dialCode },
    });
  };

  const handleSubmit = async (event: any) => {
    let usernameData;
    const data = new FormData(event.target);
    event.preventDefault();

    const regex = /[^0-9]/g;

    if (signinMethod === "email") {
      usernameData = data.get("email");
    } else if (signinMethod === "phone") {
      const originalNumber = data.get("phone") as string;
      if (originalNumber.charAt(0) === "0") {
        usernameData = `+${dialCode}${originalNumber
          .substring(1)
          .replace(regex, "")}`;
      } else {
        usernameData = `+${dialCode}${originalNumber.replace(regex, "")}`;
      }
    }

    const oauthData = {
      username: usernameData,
      password: data.get("password"),
      redirect_uri: data.get("redirect_uri"),
      state: data.get("state"),
    };

    const userData = {
      username: usernameData,
      password: data.get("password"),
    };

    signin({
      event: event,
      data: location.pathname === "/oauth" ? oauthData : userData,
      usernameError: usernameCheck.error,
      passwordError: passwordCheck.error,
      dialCode: dialCode,
      pathname: location.pathname,
    });
  };

  return (
    <TemplateFrame
      toggleCustomTheme={toggleCustomTheme}
      showCustomTheme={showCustomTheme}
      mode={mode}
      toggleColorMode={toggleColorMode}>
      <ThemeProvider theme={showCustomTheme ? SignInTheme : defaultTheme}>
        <CssBaseline enableColorScheme />
        <SignInContainer direction="column" justifyContent="space-between">
          <Card variant="outlined">
            <OdnIcon mode={mode} />
            <Box
              component="form"
              onSubmit={handleSubmit}
              method="post"
              action={
                location.pathname === "/oauth"
                  ? ENV.authUrl
                  : `${ENV.authUrl}/login`
              }
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}>
              <input
                type="hidden"
                name="redirect_uri"
                value={`${window.location.origin}`}
              />
              <input type="hidden" name="state" value={stateValue} />
              <FormControl>
                <FormLabel htmlFor={signinMethod}>
                  <Link
                    onClick={() => setSigninMethod("email")}
                    sx={{
                      cursor: "pointer",
                      fontWeight: signinMethod === "email" ? "bold" : "lighter",
                      color: signinMethod === "email" ? "" : "gray",
                    }}>
                    {dialCode === "82" ? "이메일" : "Email"}
                  </Link>

                  <span style={{ fontWeight: "bolder" }}>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                  </span>

                  <Link
                    onClick={() => setSigninMethod("phone")}
                    sx={{
                      cursor: "pointer",
                      fontWeight: signinMethod === "phone" ? "bold" : "lighter",
                      color: signinMethod === "phone" ? "" : "gray",
                    }}>
                    {dialCode === "82" ? "전화번호" : "Phone"}
                  </Link>
                </FormLabel>

                <TextField
                  id="username"
                  type="email"
                  name="email"
                  placeholder="example@email.com"
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  error={usernameCheck.error}
                  helperText={usernameCheck.message}
                  color={usernameCheck.error ? "error" : "primary"}
                  sx={{
                    ariaLabel: "email",
                    display: signinMethod === "email" ? "" : "none",
                  }}
                />
                <TextField
                  required
                  autoFocus
                  fullWidth
                  name="phone"
                  type="tel"
                  id="phone"
                  // placeholder="01000000000"
                  autoComplete="tel"
                  variant="outlined"
                  error={usernameCheck.error}
                  helperText={usernameCheck.message}
                  color={usernameCheck.error ? "error" : "primary"}
                  sx={{
                    ariaLabel: "tel",
                    display: signinMethod === "phone" ? "" : "none",
                  }}
                />
              </FormControl>
              <FormControl>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <FormLabel htmlFor="password">
                    {dialCode === "82" ? "비밀번호" : "Password"}
                  </FormLabel>
                </Box>
                <PasswordTextField
                  passwordCheck={passwordCheck}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
              </FormControl>
              {/* <FormControlLabel
                control={<Checkbox value="remember" color="primary" />}
                label="Remember me"
              /> */}

              <Button
                sx={{ marginTop: 1 }}
                type="submit"
                fullWidth
                variant="contained"
                onClick={validateInputs}>
                {dialCode === "82" ? "로그인" : "Log in"}
              </Button>
            </Box>

            <Divider>or</Divider>

            <Typography sx={{ textAlign: "center" }}>
              <span>
                <Link
                  href="/signup"
                  variant="body2"
                  sx={{ alignSelf: "center" }}>
                  {dialCode === "82" ? "회원가입" : "Sign up"}
                </Link>
              </span>

              <span style={{ marginLeft: 10, marginRight: 10 }}>|</span>

              <span>
                <Link
                  href="/reset-password"
                  variant="body2"
                  sx={{ alignSelf: "center" }}>
                  {dialCode === "82" ? "비밀번호 변경" : "Reset password"}
                </Link>
              </span>
              {/* 
              <span style={{ marginLeft: 10, marginRight: 10 }}>|</span>

              <span>
                <Link
                  target="_blank"
                  rel="noopener"
                  href={
                    dialCode === "84"
                      ? "https://odn-dashboard.notion.site/ODN-Dashboard-Manual-VN-175f77b5f3b7819e80b7f25763784fd5"
                      : dialCode === "82"
                      ? "https://odn-dashboard.notion.site/ODN-175f77b5f3b7816eaf04e39713bcac25"
                      : "https://odn-dashboard.notion.site/ODN-Dashboard-Manual-EN-175f77b5f3b781c890c4ee09bb3e37c4"
                  }
                  variant="body2"
                  sx={{ alignSelf: "center" }}>
                  {dialCode === "84"
                    ? "Manual (VN)"
                    : dialCode === "82"
                    ? "Manual (KR)"
                    : "Manual (EN)"}
                </Link>
              </span> */}
            </Typography>

            {/* Kakao Login Button */}
            <Divider sx={{ display: "none" }}>or</Divider>
            <Box
              sx={{
                display: "none", // "flex"
                flexDirection: "column",
                gap: 2,
              }}>
              <Button
                style={{
                  backgroundColor: "#FFE812",
                  color: "black",
                }}
                type="submit"
                fullWidth
                // variant="outlined"
                onClick={() => alert("Sign in with Kakao")}
                startIcon={<KakaoIcon />}>
                Sign in with Kakao
              </Button>
            </Box>
            {/* Kakao Login Button */}
          </Card>
        </SignInContainer>
      </ThemeProvider>
    </TemplateFrame>
  );
}
